<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { useUserStore } from '@/authentication/user.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { isBetaOptIn } from '@/util/beta_opt_in'
import { goalbookUrls } from '@/util/links'
import { FROM_HOME, HOME_EVENT_NAMES } from './event_names'
import { instructionalDesignCards } from './instructional_design_cards'
import { TRAINING_CENTER_LINK_CLASSES } from './training_center_styles'
import InstructionalDesignCard from './InstructionalDesignCard.vue'
import ThreadsCardLink from './ThreadsCardLink.vue'
import GrowCardLink from './GrowCardLink.vue'
import Hb2CardLink from './Hb2CardLink.vue'
import RecentLibraryFolders from './RecentLibraryFolders.vue'
import HomePageRecentlyViewed from './HomePageRecentlyViewed.vue'
import OnDemandTraining from './OnDemandTraining.vue'
import UpcomingWebinars from './UpcomingWebinars.vue'
import RecentUpdates from './RecentUpdates.vue'

const route = useRoute()
const analytics = getAnalyticsTracker()
const { userFirstName } = storeToRefs(useUserStore())
const {
  betaVisibility,
  iodVisibility,
  effectsVisibility,
  aiFeatureVisibility,
  nycWebinarsVisibility,
  threadsVisibility,
  growVisibility,
  hb2Visibility,
  isCcsdProfile,
  transitionThreadVisibility,
  bspThreadVisibility
} = storeToRefs(useVisibilityStore())

const isBetaKept = computed(() => betaVisibility.value || isBetaOptIn(route.query))

const cards = computed(() => instructionalDesignCards({
  isIodVisible: iodVisibility.value,
  isEffectsVisible: effectsVisibility.value,
  isAiFeatureVisible: aiFeatureVisibility.value,
  isBetaKept: isBetaKept.value
}))

const isOnDemandTrainingShown = computed(() => aiFeatureVisibility.value || nycWebinarsVisibility.value)

const SPANS_FULL_ROW = 'col-span-full'
const ON_DEMAND_TRAINING_AREA = 'md:max-xl:col-start-1 md:max-xl:row-start-1'
const RECENT_UPDATES_AREA = 'md:max-xl:col-start-1 md:max-xl:row-start-2'
const WEBINARS_AREA = 'md:max-xl:col-start-2 md:max-xl:row-start-2'
const SECTION_TITLE_CLASSES = 'mb-[10px] mt-5 text-[1rem] font-[600] uppercase leading-[1.1] text-[#424141]'

function trackNycWebinarsClick() {
  analytics.sendEvent(HOME_EVENT_NAMES.CLICKED_NYC_UPCOMING_WEBINARS, FROM_HOME)
}

analytics.sendEvent(HOME_EVENT_NAMES.VIEWED_SIGNED_IN_LANDING_PAGE, { vue: true, ref: route.query.ref })
</script>

<template>
  <div class="container pt-5 text-[1rem] text-gray-900">
    <section>
      <h1 class="mb-[10px] mt-0 text-[2rem] font-[600] leading-[1.1] text-[#424141]">Welcome, {{ userFirstName }}!</h1>
      <h2 :class="SECTION_TITLE_CLASSES">Instructional Design Tools</h2>
      <!-- Threads leads the Instructional Design Tools, ahead of the three tool cards; mb-5 matches their gap-5 -->
      <ThreadsCardLink
        v-if="threadsVisibility"
        :is-ccsd="isCcsdProfile"
        :is-transition-visible="transitionThreadVisibility"
        :is-bsp-visible="bspThreadVisibility"
        class="mb-5"
      />
    </section>
    <section class="mx-auto mb-6 grid max-w-full grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-5 max-xl:grid-cols-[repeat(2,1fr)] max-[992px]:grid-cols-[1fr]">
      <InstructionalDesignCard
        v-for="card in cards"
        :key="card.key"
        :data-testid="`instructional-design-card-${card.key}`"
        :accent-color="card.accentColor"
        :hover-color="card.hoverColor"
        :img-src="card.imgSrc"
        :header="card.header"
        :card-description="card.cardDescription"
        :button="card.button"
        :other-links="card.otherLinks"
      />
      <GrowCardLink v-if="growVisibility" :class="SPANS_FULL_ROW" />
      <Hb2CardLink v-if="hb2Visibility" :class="SPANS_FULL_ROW" />
    </section>
    <section class="grid grid-cols-[2fr_1fr] gap-10 max-xl:grid-cols-[1fr]">
      <div>
        <h2 :class="SECTION_TITLE_CLASSES">Your Content</h2>
        <RecentLibraryFolders />
        <HomePageRecentlyViewed />
      </div>
      <div class="grid grid-rows-[repeat(3,min-content)] md:max-xl:grid-cols-[2fr_1fr] md:max-xl:grid-rows-[min-content_min-content] max-md:grid-cols-[1fr]">
        <OnDemandTraining v-if="isOnDemandTrainingShown" :class="ON_DEMAND_TRAINING_AREA" />
        <UpcomingWebinars v-if="aiFeatureVisibility" :class="WEBINARS_AREA" />
        <div v-if="nycWebinarsVisibility" :class="WEBINARS_AREA" data-testid="nyc-webinars">
          <h3 class="mb-[10px] mt-5 text-[24px] font-medium leading-[1.1]">Upcoming Webinars</h3>
          <a
            :href="goalbookUrls.nycWebinars"
            :class="TRAINING_CENTER_LINK_CLASSES"
            target="_blank"
            rel="noopener"
            @click="trackNycWebinarsClick"
          >
            View upcoming webinars for NYC
            <span class="sr-only">(opens in new tab)</span>
          </a>
        </div>
        <RecentUpdates v-if="aiFeatureVisibility" :class="RECENT_UPDATES_AREA" />
      </div>
    </section>
  </div>
</template>
